import React, { useEffect, useRef, useState } from 'react';
import { RESOLUTIONS } from '../utils/cameraSetup';

const STATUS = {
  idle: 'Camera off',
  live: 'Camera on',
  recording: 'Recording',
  saving: 'Saving',
  error: 'Needs attention',
};

function cameraChoice(device) {
  const known = device.cameras?.some((cam) => cam.deviceId && cam.deviceId === device.settings.cameraId);
  if (device.settings.cameraId && known) return `device:${device.settings.cameraId}`;
  return `facing:${device.settings.facing || 'environment'}`;
}

export default function RecordControl() {
  const [devices, setDevices] = useState([]);
  const [folder, setFolder] = useState('');
  const [pairCode, setPairCode] = useState('');
  const [phoneUrl, setPhoneUrl] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [message, setMessage] = useState('');
  const [authed, setAuthed] = useState(null);
  const [password, setPassword] = useState('');
  const socketRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    fetch('/api/session')
      .then((response) => { if (!cancel) setAuthed(response.ok); })
      .catch(() => { if (!cancel) setAuthed(false); });
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (authed !== true) return undefined;
    let cancel = false;
    let socket;
    let retry;
    function connect() {
      if (cancel) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(`${protocol}//${window.location.host}/ws?role=operator`);
      socketRef.current = socket;
      socket.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (data.type === 'state') {
          setDevices(data.devices || []);
          setFolder(data.folder || '');
          setPairCode(data.pairCode || '');
          setPhoneUrl(data.phoneUrl || '');
          setMessage('');
        } else if (data.type === 'error') {
          setMessage(data.error || 'The phone did not accept that.');
        }
      };
      socket.onclose = (event) => {
        if (cancel) return;
        if (event.code === 4401) {
          setAuthed(false);
          return;
        }
        retry = setTimeout(connect, 1000);
      };
    }
    connect();
    return () => {
      cancel = true;
      clearTimeout(retry);
      socket?.close();
    };
  }, [authed]);

  useEffect(() => {
    if (!selectedId && devices.length === 1) setSelectedId(devices[0].id);
  }, [devices, selectedId]);

  const selected = devices.find((device) => device.id === selectedId) || null;
  const locked = selected?.status === 'recording' || selected?.status === 'saving';

  function push(payload) {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== 1) {
      setMessage('Reconnecting to the recording server.');
      return;
    }
    setMessage('');
    socket.send(JSON.stringify(payload));
  }

  function send(id, action, settings) {
    push({ type: 'command', id, action, settings });
  }

  async function unlock(event) {
    event.preventDefault();
    const response = await fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setMessage(data.error || 'The key was not accepted.');
      return;
    }
    setPassword('');
    setAuthed(true);
  }

  function onCameraChange(value) {
    if (!selected) return;
    if (value.startsWith('device:')) {
      const cameraId = value.slice('device:'.length);
      const cam = selected.cameras.find((item) => item.deviceId === cameraId);
      send(selected.id, 'settings', {
        cameraId,
        facing: cam?.facing || selected.settings.facing,
        width: selected.settings.width,
        height: selected.settings.height,
      });
      return;
    }
    send(selected.id, 'settings', {
      cameraId: '',
      facing: value.slice('facing:'.length),
      width: selected.settings.width,
      height: selected.settings.height,
    });
  }

  function onResolutionChange(id) {
    const preset = RESOLUTIONS.find((item) => item.id === id);
    if (!preset || !selected) return;
    send(selected.id, 'settings', {
      ...selected.settings,
      width: preset.width,
      height: preset.height,
    });
  }

  if (authed === null) {
    return <p className="text-neutral-400 p-6">Checking the recording server...</p>;
  }

  if (authed !== true) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white">
        <form onSubmit={unlock} className="max-w-md mx-auto px-6 py-16 flex flex-col gap-3">
          <h1 className="text-2xl font-semibold m-0">Record control</h1>
          <p className="text-neutral-400 m-0">Enter the operator key from the QURE Recordings folder on this computer.</p>
          <label className="text-sm text-neutral-400" htmlFor="operator-key">Operator key</label>
          <input
            id="operator-key"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="rounded-xl bg-neutral-900 text-white px-3 py-3 border border-neutral-700"
          />
          <button type="submit" className="rounded-full bg-white text-black font-semibold py-3 border-0" style={{ width: '100%' }}>
            Unlock
          </button>
          {message && <p className="text-red-400 m-0" role="alert">{message}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-semibold m-0">Record control</h1>
        <p className="text-neutral-400 mt-2 mb-0">
          Phones pair on this Wi-Fi. Start and stop stay on this page. Video never leaves the network.
        </p>
        {phoneUrl && <p className="text-sm text-neutral-300 mt-2 mb-0">Phone page: {phoneUrl}</p>}
        {pairCode && (
          <div className="mt-4 flex items-center gap-4">
            <div>
              <p className="text-sm text-neutral-500 m-0">Pairing code</p>
              <p className="text-4xl tracking-widest font-semibold m-0">{pairCode}</p>
            </div>
            <button
              type="button"
              onClick={() => push({ type: 'rotate-code' })}
              className="rounded-full bg-neutral-800 text-white px-4 py-2 border-0"
            >
              New code
            </button>
          </div>
        )}
        {folder && <p className="text-sm text-neutral-500 mt-2 mb-0">Videos save to {folder}</p>}

        <section className="mt-6">
          <h2 className="text-sm uppercase tracking-wide text-neutral-500 m-0">Phones</h2>
          {devices.length === 0 && (
            <p className="mt-3 text-neutral-400">No phones yet. Open the phone page and it will show up here.</p>
          )}
          <ul className="mt-3 flex flex-col gap-2 p-0 list-none">
            {devices.map((device) => {
              const active = device.id === selected?.id;
              const connected = device.status === 'live' || device.status === 'recording' || device.status === 'saving';
              return (
                <li key={device.id}>
                  <div className={`rounded-2xl border px-4 py-3 flex items-center gap-3 ${active ? 'border-white bg-neutral-900' : 'border-neutral-800'}`}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(device.id)}
                      className="flex-1 text-left bg-transparent text-white border-0 p-0"
                    >
                      <span className="block font-semibold">{device.name || 'Phone'}</span>
                      <span className="block text-sm text-neutral-400">
                        {device.online ? 'Online' : 'Offline'} · {STATUS[device.status] || device.status}
                      </span>
                    </button>
                    <button
                      type="button"
                      disabled={!device.online || device.status === 'saving'}
                      onClick={() => {
                        setSelectedId(device.id);
                        send(device.id, connected ? 'disconnect' : 'connect', device.settings);
                      }}
                      className="rounded-full bg-neutral-800 text-white px-4 py-2 border-0 disabled:opacity-40"
                    >
                      {connected ? 'Disconnect' : 'Connect'}
                    </button>
                    <button
                      type="button"
                      onClick={() => push({ type: 'remove', id: device.id })}
                      className="rounded-full bg-neutral-800 text-white px-4 py-2 border-0"
                    >
                      Remove
                    </button>
                  </div>
                  {device.error && <p className="text-sm text-red-400 mt-1 mb-0 px-2">{device.error}</p>}
                </li>
              );
            })}
          </ul>
        </section>

        {selected && (
          <section className="mt-6 rounded-2xl border border-neutral-800 p-4">
            <h2 className="text-lg font-semibold m-0">{selected.name || 'Phone'}</h2>
            <p className="text-sm text-neutral-400 mt-1 mb-4">{STATUS[selected.status] || selected.status}</p>
            {selected.online && selected.ready === false && (
              <p className="text-sm text-amber-300 mt-0 mb-4">
                On the phone, tap Allow camera and microphone once. After that, start and stop stay here.
              </p>
            )}

            <label className="block text-sm text-neutral-400" htmlFor="camera-choice">Camera</label>
            <select
              id="camera-choice"
              value={cameraChoice(selected)}
              disabled={!selected.online || locked}
              onChange={(event) => onCameraChange(event.target.value)}
              className="mt-1 mb-4 w-full rounded-xl bg-neutral-900 text-white px-3 py-3 border border-neutral-700"
            >
              <option value="facing:environment">Rear camera</option>
              <option value="facing:user">Front camera</option>
              {selected.cameras?.filter((cam) => cam.deviceId).map((cam) => (
                <option key={cam.deviceId} value={`device:${cam.deviceId}`}>
                  {cam.label}{cam.facing === 'user' ? ' · front' : cam.facing === 'environment' ? ' · rear' : ''}
                </option>
              ))}
            </select>

            <label className="block text-sm text-neutral-400" htmlFor="resolution-choice">Resolution and orientation</label>
            <select
              id="resolution-choice"
              value={`${selected.settings.width}x${selected.settings.height}`}
              disabled={!selected.online || locked}
              onChange={(event) => onResolutionChange(event.target.value)}
              className="mt-1 mb-4 w-full rounded-xl bg-neutral-900 text-white px-3 py-3 border border-neutral-700"
            >
              {RESOLUTIONS.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>

            <div className="flex gap-3">
              <button
                type="button"
                disabled={!selected.online || locked}
                onClick={() => send(selected.id, 'start', selected.settings)}
                className="rounded-full bg-red-600 text-white text-lg font-semibold py-3 border-0 disabled:opacity-40"
                style={{ width: '100%' }}
              >
                Start
              </button>
              <button
                type="button"
                disabled={selected.status !== 'recording'}
                onClick={() => send(selected.id, 'stop')}
                className="rounded-full bg-white text-black text-lg font-semibold py-3 border-0 disabled:opacity-40"
                style={{ width: '100%' }}
              >
                Stop
              </button>
            </div>
            {selected.savedPath && (
              <p className="text-sm text-green-400 mt-4 mb-0">Last video: {selected.savedPath}</p>
            )}
          </section>
        )}

        {message && <p className="text-red-400 mt-4 mb-0" role="alert">{message}</p>}
      </div>
    </div>
  );
}
